<template>
  <!-- A sidebar section with sub pages, such as Jobs (Job Listings, CV Builder).
       Expanded: a heading that only opens and closes the group, with the pages under it as the only
       links. Collapsed: the sidebar has no room for a heading, so each page is its own icon. -->
  <template v-if="collapsed">
    <SidebarLink v-for="item in items" :key="item.to" :to="item.to" :active="isActive(item)" :label="item.label">
      <template #icon>
        <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" :d="item.icon"/>
        </svg>
      </template>
    </SidebarLink>
  </template>

  <template v-else>
    <button type="button" @click="open = !open" :aria-expanded="open"
      class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors duration-150"
      :class="insideGroup ? 'bg-white/[0.15] text-white' : 'text-white/65 hover:bg-white/[0.08] hover:text-white'">
      <span class="flex-shrink-0 w-[18px] h-[18px] flex items-center justify-center">
        <svg class="w-[18px] h-[18px]" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" :d="icon"/>
        </svg>
      </span>
      <span class="flex-1 text-[13px] font-medium leading-none text-left">{{ label }}</span>
      <svg class="w-3 h-3 flex-shrink-0 transition-transform duration-200" :class="open ? 'rotate-90' : ''" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
      </svg>
    </button>

    <Transition enter-active-class="transition-all ease-out duration-200 overflow-hidden" enter-from-class="opacity-0 max-h-0" enter-to-class="opacity-100 max-h-96"
      leave-active-class="transition-all ease-in duration-150 overflow-hidden" leave-from-class="opacity-100 max-h-96" leave-to-class="opacity-0 max-h-0">
      <div v-if="open" class="ml-4 mt-0.5 mb-0.5 pl-3 border-l border-white/[0.15] space-y-0.5">
        <RouterLink v-for="item in items" :key="item.to" :to="item.to" data-turbo-prefetch="false" :aria-current="isActive(item) ? 'page' : undefined"
          class="flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-medium transition-colors duration-150"
          :class="isActive(item) ? 'bg-white/[0.15] text-white' : 'text-white/55 hover:bg-white/[0.08] hover:text-white'">
          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" :d="item.icon"/>
          </svg>
          {{ item.label }}
        </RouterLink>
      </div>
    </Transition>
  </template>
</template>

<script setup>
import { computed, inject, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { useGlobalStore } from 'stores/global.js'
import SidebarLink from './SidebarLink.vue'

const props = defineProps({
  label: { type: String, required: true },
  icon: { type: String, required: true }, // path data for the group's own icon
  items: { type: Array, required: true }, // [{ to, label, icon }]
})

const store = useGlobalStore()
const collapsed = inject('sidebarCollapsed', ref(false))

const matches = (item) => Boolean(store.currentPath?.startsWith(item.to))
// Only the most specific item is lit: on /invoices/estimates that is Estimates, not Invoices as well.
const isActive = (item) => matches(item) && !props.items.some((other) => other.to.length > item.to.length && other.to.startsWith(item.to) && matches(other))
const insideGroup = computed(() => props.items.some(matches))

// Open while you are on one of its pages, and closed when you go anywhere else, as the Analytics group
// does. A click on the heading is left alone until the next navigation.
const open = ref(insideGroup.value)
watch(() => store.currentPath, () => { open.value = insideGroup.value })
</script>
